import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  ContentCopy as CopyIcon,
  Delete as DeleteIcon,
  DeleteSweep as DeleteSweepIcon,
} from '@mui/icons-material';
import * as api from '../services/api';

interface Task {
  id: string;
  n8nExecutionId: string;
  n8nWorkflowId: string;
  formTemplateId: string;
  formTemplate?: {
    id: string;
    name: string;
    description?: string;
  };
  status: 'pending' | 'claimed' | 'in_progress' | 'completed' | 'failed';
  priority: 'low' | 'medium' | 'high' | 'critical';
  taskData: Record<string, any>;
  resultData: Record<string, any> | null;
  metadata: Record<string, any>;
  claimedBy: { username: string; displayName?: string } | null;
  claimedAt: string | null;
  startedAt: string | null;
  completedAt: string | null;
  errorMessage: string | null;
  createdAt: string;
  updatedAt: string;
}

const QueueManagement: React.FC = () => {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [priorityFilter, setPriorityFilter] = useState<string>('');
  const [n8nWorkflowFilter, setN8nWorkflowFilter] = useState<string>('');
  const [payloadModalOpen, setPayloadModalOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<'single' | 'bulk'>('single');
  const [singleDeleteId, setSingleDeleteId] = useState<string | null>(null);

  const fetchTasks = async () => {
    try {
      setLoading(true);
      setError(null);

      const params: any = {};
      if (statusFilter) params.status = statusFilter;
      if (priorityFilter) params.priority = priorityFilter;
      if (n8nWorkflowFilter) params.n8nWorkflowId = n8nWorkflowFilter;

      const response = await api.getAllTasks(params);
      setTasks(response.data.tasks || []);
      setSelectedIds(new Set());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTasks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, priorityFilter, n8nWorkflowFilter]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'default';
      case 'claimed': return 'info';
      case 'in_progress': return 'warning';
      case 'completed': return 'success';
      case 'failed': return 'error';
      default: return 'default';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'low': return '#4caf50';
      case 'medium': return '#ff9800';
      case 'high': return '#f44336';
      case 'critical': return '#9c27b0';
      default: return '#757575';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const handleViewPayload = (task: Task) => {
    setSelectedTask(task);
    setPayloadModalOpen(true);
  };

  const handleClosePayloadModal = () => {
    setPayloadModalOpen(false);
    setSelectedTask(null);
  };

  const handleCopyPayload = () => {
    if (!selectedTask) return;
    const payloadText = JSON.stringify(selectedTask.taskData, null, 2);
    navigator.clipboard.writeText(payloadText).catch(() => {});
  };

  // Selection handlers
  const filteredTasks = tasks.filter(task => {
    if (statusFilter === '' && (task.status === 'completed' || task.status === 'failed')) {
      return false;
    }
    return true;
  });

  const handleToggleSelect = (taskId: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(taskId)) {
        next.delete(taskId);
      } else {
        next.add(taskId);
      }
      return next;
    });
  };

  const handleSelectAll = () => {
    if (selectedIds.size === filteredTasks.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredTasks.map(t => t.id)));
    }
  };

  // Delete handlers
  const handleDeleteSingle = (taskId: string) => {
    setSingleDeleteId(taskId);
    setDeleteTarget('single');
    setDeleteConfirmOpen(true);
  };

  const handleDeleteSelected = () => {
    setDeleteTarget('bulk');
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      setError(null);
      if (deleteTarget === 'single' && singleDeleteId) {
        await api.deleteTask(singleDeleteId);
      } else if (deleteTarget === 'bulk') {
        await api.deleteTasks(Array.from(selectedIds));
      }
      setDeleteConfirmOpen(false);
      setSingleDeleteId(null);
      await fetchTasks();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete tasks');
      setDeleteConfirmOpen(false);
    }
  };

  const deleteCount = deleteTarget === 'single' ? 1 : selectedIds.size;

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h4">
          Task Management
        </Typography>
        {selectedIds.size > 0 && (
          <Button
            variant="contained"
            color="error"
            startIcon={<DeleteSweepIcon />}
            onClick={handleDeleteSelected}
          >
            Delete Selected ({selectedIds.size})
          </Button>
        )}
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
            <Box flex="1" minWidth="200px">
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  label="Status"
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="claimed">Claimed</MenuItem>
                  <MenuItem value="in_progress">In Progress</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="failed">Failed</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box flex="1" minWidth="200px">
              <FormControl fullWidth size="small">
                <InputLabel>Priority</InputLabel>
                <Select
                  value={priorityFilter}
                  onChange={(e) => setPriorityFilter(e.target.value)}
                  label="Priority"
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="low">Low</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="critical">Critical</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box flex="1" minWidth="200px">
              <TextField
                fullWidth
                size="small"
                label="n8n Workflow ID"
                value={n8nWorkflowFilter}
                onChange={(e) => setN8nWorkflowFilter(e.target.value)}
              />
            </Box>
            <Box flex="1" minWidth="200px">
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={fetchTasks}
                fullWidth
              >
                Refresh
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={selectedIds.size > 0 && selectedIds.size < filteredTasks.length}
                    checked={filteredTasks.length > 0 && selectedIds.size === filteredTasks.length}
                    onChange={handleSelectAll}
                  />
                </TableCell>
                <TableCell>Form Name</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Priority</TableCell>
                <TableCell>Claimed By</TableCell>
                <TableCell>Completed By</TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredTasks.map((task) => (
                <TableRow
                  key={task.id}
                  selected={selectedIds.has(task.id)}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedIds.has(task.id)}
                      onChange={() => handleToggleSelect(task.id)}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {task.formTemplate?.name || 'Unknown Form'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={task.status}
                      color={getStatusColor(task.status) as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={task.priority}
                      size="small"
                      sx={{
                        backgroundColor: getPriorityColor(task.priority),
                        color: 'white'
                      }}
                    />
                  </TableCell>
                  <TableCell>{task.claimedBy?.displayName || task.claimedBy?.username || '-'}</TableCell>
                  <TableCell>
                    {task.status === 'completed'
                      ? task.metadata?.completedBy || task.claimedBy?.displayName || task.claimedBy?.username || '-'
                      : '-'}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatDate(task.createdAt)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Box display="flex" gap={0.5} justifyContent="flex-end">
                      <Tooltip title="View Payload">
                        <IconButton
                          size="small"
                          onClick={() => handleViewPayload(task)}
                          color="info"
                        >
                          <ViewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteSingle(task.id)}
                          color="error"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
              {filteredTasks.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography variant="body1" color="text.secondary">
                      No tasks found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Payload Modal */}
      <Dialog
        open={payloadModalOpen}
        onClose={handleClosePayloadModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Task Payload
          {selectedTask && (
            <Typography variant="subtitle2" color="text.secondary">
              Task ID: {selectedTask.id}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent dividers>
          {selectedTask && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Task Data
              </Typography>
              <Paper
                sx={{
                  p: 2,
                  backgroundColor: '#f5f5f5',
                  maxHeight: '400px',
                  overflow: 'auto',
                  mb: 2
                }}
              >
                <pre style={{ margin: 0, fontSize: '12px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {JSON.stringify(selectedTask.taskData, null, 2)}
                </pre>
              </Paper>

              {selectedTask.metadata && Object.keys(selectedTask.metadata).length > 0 && (
                <>
                  <Typography variant="h6" gutterBottom>
                    Metadata
                  </Typography>
                  <Paper
                    sx={{
                      p: 2,
                      backgroundColor: '#f5f5f5',
                      maxHeight: '200px',
                      overflow: 'auto',
                      mb: 2
                    }}
                  >
                    <pre style={{ margin: 0, fontSize: '12px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {JSON.stringify(selectedTask.metadata, null, 2)}
                    </pre>
                  </Paper>
                </>
              )}

              {selectedTask.resultData && Object.keys(selectedTask.resultData).length > 0 && (
                <>
                  <Typography variant="h6" gutterBottom>
                    Result Data
                  </Typography>
                  <Paper
                    sx={{
                      p: 2,
                      backgroundColor: '#f5f5f5',
                      maxHeight: '200px',
                      overflow: 'auto'
                    }}
                  >
                    <pre style={{ margin: 0, fontSize: '12px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {JSON.stringify(selectedTask.resultData, null, 2)}
                    </pre>
                  </Paper>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCopyPayload}
            startIcon={<CopyIcon />}
            variant="outlined"
          >
            Copy to Clipboard
          </Button>
          <Button onClick={handleClosePayloadModal} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete {deleteCount} task{deleteCount !== 1 ? 's' : ''}? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default QueueManagement;
